<template>
  <v-row class="justify-center">
    <!-- Expand / Contract -->
    <div class="d-flex flex-column flex-sm-row align-center pa-3">
      <div class="mr-0 mr-sm-4">
        <v-btn color="primary" small outlined class="mb-2 mb-sm-0" @click="$emit('expand-all')">
          <span class="white--text">{{ $t('instructions.expandAll') }}</span>
        </v-btn>
      </div>
      <div>
        <v-btn color="primary" small outlined @click="$emit('contract-all')">
          <span class="white--text">{{ $t('instructions.contractAll') }}</span>
        </v-btn>
      </div>
    </div>

    <!-- Date pickers -->
    <v-col
      v-if="dateFilterEnabled"
      class="d-flex flex-column flex-sm-row align-center"
    >
      <!-- Start date -->
      <v-menu
        v-model="menuStart"
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="dateStart"
            :label="$t('extras.start')"
            prepend-icon="mdi-calendar"
            readonly
            dense
            v-bind="attrs"
            v-on="on"
            style="width: 170px;"
            hide-details
            class="mr-0 mr-sm-6 mb-2"
          />
        </template>
        <v-date-picker
          :value="dateStart"
          no-title
          color="primary"
          :locale="locale"
          :max="dateEnd"
          @input="onStartInput"
        />
      </v-menu>

      <!-- End date -->
      <v-menu
        v-model="menuEnd"
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="dateEnd"
            :label="$t('extras.end')"
            prepend-icon="mdi-calendar"
            readonly
            dense
            v-bind="attrs"
            v-on="on"
            style="width: 170px;"
            hide-details
            class="mr-0 mr-sm-6 mb-2"
          />
        </template>
        <v-date-picker
          :value="dateEnd"
          no-title
          color="primary"
          :locale="locale"
          :min="dateStart"
          @input="onEndInput"
        />
      </v-menu>

      <!-- Status filter -->
      <v-select
        :value="statusFilter"
        :items="statusFilterItems"
        item-text="label"
        item-value="value"
        :label="statusFilterLabel"
        outlined
        dense
        style="max-width: 250px; margin-bottom: -15px;"
        class="mr-4"
        clearable
        @input="$emit('update:statusFilter', $event)"
      />

      <!-- Broker-only filters -->
      <template v-if="isBroker">
        <v-select
          :value="filterValue"
          :items="filterItems"
          :item-text="filterItemText"
          :item-value="filterItemValue"
          :label="filterLabel"
          outlined
          dense
          style="max-width: 250px; margin-bottom: -15px;"
          class="mr-4"
          clearable
          @input="$emit('update:filterValue', $event)"
        />
        <v-radio-group
          :value="sideFilter"
          row
          @change="$emit('update:sideFilter', $event)"
        >
          <v-radio :label="$t('quoteInfo.buy')" value="BUY" />
          <v-radio :label="$t('quoteInfo.sell')" value="SELL" />
          <v-radio :label="$t('extras.both')" value="BOTH" />
        </v-radio-group>
        <v-btn
          color="primary"
          small
          outlined
          @click="$emit('reset-filter')"
        >
          <span class="white--text">{{ $t('instructions.resetFilter') }}</span>
        </v-btn>
      </template>
    </v-col>

    <v-spacer v-if="!isBroker" class="d-none d-lg-flex" />

    <!-- Export buttons -->
    <div
      :class="isBroker || isPartner
        ? 'd-flex flex-row px-3 justify-start flex-row-reverse flex-gap-4'
        : 'd-flex flex-column flex-sm-row-reverse pa-3 align-center'"
      :style="isBroker || isPartner ? 'width: 100%' : ''"
    >
      <div>
        <v-btn
          color="primary"
          small
          outlined
          class="mb-2 mb-sm-0"
          @click="$emit('export-pdf')"
        >
          <span class="white--text">{{ $t('instructions.exportPdf') }}</span>
        </v-btn>
      </div>
      <div class="mr-0 mr-sm-4">
        <v-btn
          data-testid="reports-export-csv"
          color="primary"
          small
          outlined
          class="mb-2 mb-sm-0"
          @click="$emit('export-csv')"
        >
          <span class="white--text">{{ $t('instructions.exportSpreadsheet') }}</span>
        </v-btn>
      </div>
    </div>
  </v-row>
</template>

<script>
export default {
  name: 'ReportsToolbar',
  props: {
    dateStart:       { type: String, default: '' },
    dateEnd:         { type: String, default: '' },
    dateFilterEnabled: { type: Boolean, default: true },
    filterValue:     { type: [String, Number], default: null },
    filterItems:     { type: Array, default: () => [] },
    filterLabel:     { type: String, default: '' },
    filterItemText:  { type: String, default: 'label' },
    filterItemValue: { type: String, default: 'id' },
    sideFilter:      { type: String, default: 'BOTH' },
    statusFilter:      { type: String, default: null },
    statusFilterItems: { type: Array, default: () => [] },
    statusFilterLabel: { type: String, default: '' },
    isBroker:        { type: Boolean, default: false },
    isPartner:       { type: Boolean, default: false },
    locale:          { type: String, default: 'en' },
    disableExport:   { type: Boolean, default: false },
  },
  emits: [
    'update:dateStart', 'update:dateEnd',
    'update:filterValue', 'update:sideFilter', 'update:statusFilter',
    'reset-filter', 'expand-all', 'contract-all',
    'export-pdf', 'export-csv',
  ],
  data () {
    return {
      menuStart: false,
      menuEnd:   false,
    }
  },
  methods: {
    onStartInput (val) {
      this.menuStart = false
      this.$emit('update:dateStart', val)
    },
    onEndInput (val) {
      this.menuEnd = false
      this.$emit('update:dateEnd', val)
    },
  },
}
</script>

<style scoped>
::v-deep .v-input--radio-group--row .v-input--radio-group__input {
  flex-wrap: nowrap !important;
}
</style>
